import { useEffect, useMemo, useState } from 'react';
import { Modal, Select, Alert, Typography, Space, Radio } from 'antd';

const { Text } = Typography;

// Matches ALL in server/converge/allocations.ts — "every scenario in the
// business case". Portable across business cases, so never remapped.
const ALL = -2;

const DROP = '__drop__';

// Tolerant accessors — replace with your real shapes once confirmed.
export const getBusinessCaseId = (bc) =>
    String(bc?.business_case_id ?? '')

export const getBusinessCaseName = (bc) =>
     bc?.name ?? '';

export const getScenarioIdsForBusinessCase = (bc) =>
    (bc?.business_scenario ?? [])
        .map((r) => Number(r?.scenario_id))
        .filter(Number.isFinite);

// Flattens the entity tree into id -> scenario name.
export const buildScenarioIndex = (entities = []) => {
    const index = new Map();

    const walk = (nodes = []) => {
        nodes.forEach((node) => {
            (Array.isArray(node?.scenario) ? node.scenario : []).forEach((s) => {
                const id = Number(s?.scenario_id);
                if (Number.isFinite(id)) index.set(id, s?.name ?? s?.scenario_name ?? `#${id}`);
            });
            if (Array.isArray(node?.children)) walk(node.children);
        });
    };

    walk(entities);
    return index;
};

const sortedIds = (value) => (Array.isArray(value) ? value.map(String).sort() : []);

export const allocationSignature = (row) =>
    [
        String(row.entityId ?? ''),
        String(row.accountId ?? ''),
        sortedIds(row.scenarioId).join(','),
        String(row.targetEntityId ?? ''),
        String(row.targetAccountId ?? ''),
        sortedIds(row.targetScenarioId).join(','),
    ].join('|');

    export const getScenarioIdsForEntity = (entityId, entities = []) => {
        const out = [];
        const walk = (nodes = []) => {
            nodes.forEach((node) => {
                if (Number(node?.entity_id) === Number(entityId)) {
                    (Array.isArray(node?.scenario) ? node.scenario : []).forEach((s) => {
                        const id = Number(s?.scenario_id);
                        if (Number.isFinite(id)) out.push(id);
                    });
                }
                if (Array.isArray(node?.children)) walk(node.children);
            });
        };
        walk(entities);
        return out;
    };

const CopyAllocationsModal = ({
    open,
    sourceName,
    sourceBusinessCaseId,
    rowsToCopy,
    allRows,
    businessCases,
    entities,
    copying,
    onCancel,
    onConfirm,
}) => {
    const [targetId, setTargetId] = useState(null);
    // { [originalScenarioId]: newScenarioId | ALL | DROP }
    const [scenarioMap, setScenarioMap] = useState({});
    const [dropStranded, setDropStranded] = useState(true);

    useEffect(() => {
        if (open) {
            setTargetId(null);
            setScenarioMap({});
            setDropStranded(true);
        }
    }, [open]);

    // Changing target invalidates any mapping chosen for the previous one.
    useEffect(() => {
        setScenarioMap({});
        setDropStranded(true);
    }, [targetId]);

    const scenarioIndex = useMemo(() => buildScenarioIndex(entities), [entities]);
    const scenarioName = (id) => scenarioIndex.get(Number(id)) ?? `Scenario ${id}`;

    const targetBusinessCase = useMemo(
        () => (businessCases ?? []).find((bc) => getBusinessCaseId(bc) === targetId) ?? null,
        [businessCases, targetId],
    );

    const targetScenarioIds = useMemo(
        () => getScenarioIdsForBusinessCase(targetBusinessCase),
        [targetBusinessCase],
    );

    const targetScenarioSet = useMemo(
        () => new Set(targetScenarioIds.map(Number)),
        [targetScenarioIds],
    );
    const options = useMemo(
        () =>
            (businessCases ?? [])
                .filter((bc) => getBusinessCaseId(bc) !== String(sourceBusinessCaseId ?? ''))
                .map((bc) => ({ value: getBusinessCaseId(bc), label: getBusinessCaseName(bc) }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [businessCases, sourceBusinessCaseId],
    );

    // Mirrors expandTargetScenarioIdsForBC: ALL with an entity resolves to that
    // entity's scenarios intersected with the business case. No overlap means
    // the allocation writes nothing.
    const allResolvesToNothing = (ids, entityId) => {
        if (!(ids ?? []).map(Number).includes(ALL)) return false;
        if (entityId == null || Number(entityId) <= 0) return false;

        return !getScenarioIdsForEntity(entityId, entities).some((id) =>
            targetScenarioSet.has(Number(id)),
        );
    };

    // Distinct scenario ids referenced by the selection that don't exist in
    // the receiving business case. ALL is portable and never appears here.
    const unresolvedScenarioIds = useMemo(() => {
        if (!targetId) return [];

        const missing = new Set();

        (rowsToCopy ?? []).forEach((row) => {
            [...(row.scenarioId ?? []), ...(row.targetScenarioId ?? [])].forEach((raw) => {
                const id = Number(raw);
                if (!Number.isFinite(id) || id === ALL) return;
                if (!targetScenarioSet.has(id)) missing.add(id);
            });
        });

        return [...missing].sort((a, b) => scenarioName(a).localeCompare(scenarioName(b)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetId, rowsToCopy, targetScenarioSet, scenarioIndex]);

    const allMapped = unresolvedScenarioIds.every((id) => scenarioMap[id] != null);

    // Rewrites a scenario array through the chosen mapping. Returns null when
    // the row should be dropped entirely.
    const remap = (ids) => {
        const out = [];

        for (const raw of ids ?? []) {
            const id = Number(raw);

            if (id === ALL || targetScenarioSet.has(id)) {
                out.push(id);
                continue;
            }

            const choice = scenarioMap[id];
            if (choice == null || choice === DROP) return null;
            out.push(Number(choice));
        }

        return [...new Set(out)];
    };

    // Remap first, then dedupe — remapping can collapse rows onto each other.
    const { toCreate, skippedExisting, skippedInBatch, skippedUnmapped, strandedCount } =
    useMemo(() => {
        if (!targetId) {
            return {
                toCreate: [],
                skippedExisting: 0,
                skippedInBatch: 0,
                skippedUnmapped: 0,
                strandedCount: 0,
            };
        }

        const existing = new Set(
            (allRows ?? [])
                .filter((row) => String(row.businessCaseId ?? '') === String(targetId))
                .map(allocationSignature),
        );

        const seen = new Set();
        const next = [];
        let existingCount = 0;
        let batchCount = 0;
        let unmappedCount = 0;
        let stranded = 0;

        (rowsToCopy ?? []).forEach((row) => {
            const strandedByEntity =
                allResolvesToNothing(row.scenarioId, row.entityId) ||
                allResolvesToNothing(row.targetScenarioId, row.targetEntityId);

            if (strandedByEntity) {
                stranded += 1;
                if (dropStranded) return;
            }

            const scenarioId = remap(row.scenarioId);
            const targetScenarioId = remap(row.targetScenarioId);

            if (scenarioId == null || targetScenarioId == null) {
                unmappedCount += 1;
                return;
            }

            const remapped = { ...row, scenarioId, targetScenarioId };
            const signature = allocationSignature(remapped);

            if (existing.has(signature)) {
                existingCount += 1;
                return;
            }
            if (seen.has(signature)) {
                batchCount += 1;
                return;
            }

            seen.add(signature);
            next.push(remapped);
        });

        return {
            toCreate: next,
            skippedExisting: existingCount,
            skippedInBatch: batchCount,
            skippedUnmapped: unmappedCount,
            strandedCount: stranded,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetId, rowsToCopy, allRows, scenarioMap, targetScenarioSet, dropStranded, entities]);

    const targetLabel = targetBusinessCase ? getBusinessCaseName(targetBusinessCase) : '';

    const scenarioOptions = [
        ...targetScenarioIds.map((id) => ({ value: id, label: scenarioName(id) })),
        { value: DROP, label: "Don't copy these allocations" },
    ];

    const skipNotes = [
        skippedExisting > 0 && `${skippedExisting} already in ${targetLabel}`,
        skippedInBatch > 0 && `${skippedInBatch} duplicated after mapping`,
        skippedUnmapped > 0 && `${skippedUnmapped} dropped`,
    ].filter(Boolean);

    return (
        <Modal
            title="Copy allocations to another business case"
            open={open}
            onCancel={onCancel}
            onOk={() => onConfirm(targetId, toCreate)}
            width={560}
            okText={
                toCreate.length > 0
                    ? `Copy ${toCreate.length} allocation${toCreate.length === 1 ? '' : 's'}`
                    : 'Copy'
            }
            okButtonProps={{
                disabled: !targetId || !allMapped || toCreate.length === 0,
                loading: copying,
            }}
            destroyOnClose
        >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Text>
                    {rowsToCopy?.length ?? 0} allocation
                    {rowsToCopy?.length === 1 ? '' : 's'} from <Text strong>{sourceName}</Text>.
                </Text>

                <div>
                    <Text>Copy to</Text>
                    <Select
                        value={targetId}
                        onChange={setTargetId}
                        options={options}
                        placeholder="Select a business case"
                        style={{ width: '100%', marginTop: 8 }}
                        showSearch
                        optionFilterProp="label"
                    />
                </div>

                {unresolvedScenarioIds.length > 0 ? (
                    <Alert
                        type="warning"
                        showIcon
                       message={
                            unresolvedScenarioIds.length <= 2
                                ? `${unresolvedScenarioIds.map(scenarioName).join(' and ')} ${
                                    unresolvedScenarioIds.length === 1 ? 'is' : 'are'
                                } not in ${targetLabel}`
                                : `${unresolvedScenarioIds.length} scenarios are not in ${targetLabel}`
                        }
                        description={
                            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                <Text type="secondary">
                                    Copied as-is they would calculate to zero. Choose what each
                                    becomes in {targetLabel}.
                                </Text>

                                {unresolvedScenarioIds.map((id) => (
                                    <div key={id}>
                                        <Text>{scenarioName(id)} becomes</Text>
                                        <Select
                                            value={scenarioMap[id] ?? null}
                                            onChange={(value) =>
                                                setScenarioMap((prev) => ({ ...prev, [id]: value }))
                                            }
                                            options={scenarioOptions}
                                            placeholder="Select a scenario"
                                            style={{ width: '100%', marginTop: 4 }}
                                        />
                                    </div>
                                ))}
                            </Space>
                        }
                    />
                ) : null}

                {strandedCount > 0 ? (
                    <Alert
                        type="warning"
                        showIcon
                        message={`${strandedCount} allocation${strandedCount === 1 ? '' : 's'} use an entity with no scenarios in ${targetLabel}`}
                        description={
                            <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                <Text type="secondary">
                                    These are set to all scenarios, which resolves to nothing here.
                                </Text>
                                <Radio.Group
                                    value={dropStranded}
                                    onChange={(e) => setDropStranded(e.target.value)}
                                >
                                    <Space direction="vertical">
                                        <Radio value={true}>Leave them behind</Radio>
                                        <Radio value={false}>Copy anyway</Radio>
                                    </Space>
                                </Radio.Group>
                            </Space>
                        }
                    />
                ) : null}

                {targetId && skipNotes.length > 0 ? (
                    <Alert
                        type="info"
                        showIcon
                        message={skipNotes.join(', ')}
                        description={
                            toCreate.length > 0 ? 'Those are skipped.' : 'Nothing left to copy.'
                        }
                    />
                ) : null}
            </Space>
        </Modal>
    );
};

export default CopyAllocationsModal;
